import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart, FREE_SHIPPING_THRESHOLD } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { orderCreationApi, pointApi, couponApi, referralApi, cryptoPaymentApi } from '../../lib/supabase-api';
import { COIN_LIST, COIN_ICONS } from '../../lib/crypto-config';
import { CryptoCoin } from '../../types';
import { devLog } from '../../lib/dev-logger';
import { useToast } from '../ui/Toast';
import SubstitutionNotice from '../common/SubstitutionNotice';
import { getCoinMinAmounts, CoinMinAmounts } from '../../lib/crypto-min-amount';

// --- Country / region data ---
const US_STATES: readonly [string, string][] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

const CA_PROVINCES: readonly [string, string][] = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
  ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];

const AU_STATES: readonly [string, string][] = [
  ['ACT', 'Australian Capital Territory'], ['NSW', 'New South Wales'],
  ['NT', 'Northern Territory'], ['QLD', 'Queensland'], ['SA', 'South Australia'],
  ['TAS', 'Tasmania'], ['VIC', 'Victoria'], ['WA', 'Western Australia'],
];

const JP_PREFECTURES: readonly [string, string][] = [
  ['Hokkaido', 'Hokkaido'], ['Aomori', 'Aomori'], ['Iwate', 'Iwate'], ['Miyagi', 'Miyagi'],
  ['Akita', 'Akita'], ['Yamagata', 'Yamagata'], ['Fukushima', 'Fukushima'],
  ['Ibaraki', 'Ibaraki'], ['Tochigi', 'Tochigi'], ['Gunma', 'Gunma'],
  ['Saitama', 'Saitama'], ['Chiba', 'Chiba'], ['Tokyo', 'Tokyo'], ['Kanagawa', 'Kanagawa'],
  ['Niigata', 'Niigata'], ['Toyama', 'Toyama'], ['Ishikawa', 'Ishikawa'], ['Fukui', 'Fukui'],
  ['Yamanashi', 'Yamanashi'], ['Nagano', 'Nagano'], ['Gifu', 'Gifu'], ['Shizuoka', 'Shizuoka'],
  ['Aichi', 'Aichi'], ['Mie', 'Mie'], ['Shiga', 'Shiga'], ['Kyoto', 'Kyoto'],
  ['Osaka', 'Osaka'], ['Hyogo', 'Hyogo'], ['Nara', 'Nara'], ['Wakayama', 'Wakayama'],
  ['Tottori', 'Tottori'], ['Shimane', 'Shimane'], ['Okayama', 'Okayama'], ['Hiroshima', 'Hiroshima'],
  ['Yamaguchi', 'Yamaguchi'], ['Tokushima', 'Tokushima'], ['Kagawa', 'Kagawa'], ['Ehime', 'Ehime'],
  ['Kochi', 'Kochi'], ['Fukuoka', 'Fukuoka'], ['Saga', 'Saga'], ['Nagasaki', 'Nagasaki'],
  ['Kumamoto', 'Kumamoto'], ['Oita', 'Oita'], ['Miyazaki', 'Miyazaki'],
  ['Kagoshima', 'Kagoshima'], ['Okinawa', 'Okinawa'],
];

interface CountryConfig {
  name: string;
  zipLabel: string;
  stateLabel: string;
  stateOptions: readonly [string, string][] | null;
  zipMaxLength: number;
  zipTriggerLen: number; // min clean chars to fire auto-fill (0 = disabled)
  zippopotamCode: string;
}

const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  US: { name: 'United States', zipLabel: 'Zip Code', stateLabel: 'State', stateOptions: US_STATES, zipMaxLength: 5, zipTriggerLen: 5, zippopotamCode: 'us' },
  CA: { name: 'Canada', zipLabel: 'Postal Code', stateLabel: 'Province', stateOptions: CA_PROVINCES, zipMaxLength: 7, zipTriggerLen: 3, zippopotamCode: 'ca' },
  GB: { name: 'United Kingdom', zipLabel: 'Postcode', stateLabel: 'County', stateOptions: null, zipMaxLength: 8, zipTriggerLen: 3, zippopotamCode: 'gb' },
  AU: { name: 'Australia', zipLabel: 'Postcode', stateLabel: 'State/Territory', stateOptions: AU_STATES, zipMaxLength: 4, zipTriggerLen: 4, zippopotamCode: 'au' },
  FR: { name: 'France', zipLabel: 'Code Postal', stateLabel: 'Region', stateOptions: null, zipMaxLength: 5, zipTriggerLen: 5, zippopotamCode: 'fr' },
  DE: { name: 'Germany', zipLabel: 'PLZ', stateLabel: 'Bundesland', stateOptions: null, zipMaxLength: 5, zipTriggerLen: 5, zippopotamCode: 'de' },
  JP: { name: 'Japan', zipLabel: 'Postal Code', stateLabel: 'Prefecture', stateOptions: JP_PREFECTURES, zipMaxLength: 8, zipTriggerLen: 7, zippopotamCode: 'jp' },
};

const COUNTRY_ORDER = ['US', 'CA', 'GB', 'AU', 'FR', 'DE', 'JP'];

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isLoggedIn, user } = useAuth();
  const {
    items,
    subtotal,
    shippingFee,
    discount,
    total,
    shippingInfo,
    paymentInfo,
    setShippingInfo,
    appliedCoupon,
    appliedCouponData,
    applyCoupon,
    removeCoupon,
  } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [shippingForm, setShippingForm] = useState({
    name: '',
    phone: '',
    email: '',
    country: 'US',
    address: '',
    detailAddress: '',
    city: '',
    state: '',
    zipCode: '',
    deliveryRequest: '',
    addressEnglish: '',
    detailAddressEnglish: '',
    fullAddressEnglish: '',
    nameEnglish: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    // Crypto-only at launch. Other rails (card/bank/etc) are not wired up yet.
    method: 'crypto' as const,
    cryptoCoin: 'BTC' as CryptoCoin,
  });
  const [couponCode, setCouponCode] = useState('');
  const [pointsToUse, setPointsToUse] = useState(() => {
    // Restore points usage info passed from Cart
    const state = location.state as { pointsToUse?: number } | null;
    return state?.pointsToUse || 0;
  });
  const [availablePoints, setAvailablePoints] = useState(0); // Available reward points
  const [isProcessing, setIsProcessing] = useState(false);
  const [importAck, setImportAck] = useState(false);
  // PR 5: per-coin NOWPayments minimum so picker shows "Min: $X" upfront
  const [coinMins, setCoinMins] = useState<CoinMinAmounts | null>(null);

  // Login check
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (items.length === 0) {
      toast.warning('Your cart is empty.');
      navigate('/shop');
      return;
    }
  }, [isLoggedIn, items.length, navigate]);

  // Load reward points balance
  useEffect(() => {
    const loadPoints = async () => {
      if (user?.id) {
        const balance = await pointApi.getBalance(user.id);
        setAvailablePoints(balance);
      }
    };

    loadPoints();
  }, [user?.id]);

  // PR 5: fetch NOWPayments per-coin mins once so picker can show "Min: $X"
  useEffect(() => {
    let cancelled = false;
    getCoinMinAmounts().then(m => { if (!cancelled) setCoinMins(m); });
    return () => { cancelled = true; };
  }, []);

  // Auto-fill logged-in user info
  useEffect(() => {
    if (user && !shippingInfo) {
      setShippingForm(prev => ({
        ...prev,
        name: user.profile?.name || user.name || '',
        email: user.email || '',
        phone: user.profile?.phone || ''
      }));
    }
  }, [user, shippingInfo]);

  // Set shipping info to form if already exists (high priority)
  useEffect(() => {
    if (shippingInfo) {
      setShippingForm({
        name: shippingInfo.name,
        phone: shippingInfo.phone,
        email: shippingInfo.email,
        country: shippingInfo.country && COUNTRY_CONFIGS[shippingInfo.country] ? shippingInfo.country : 'US',
        address: shippingInfo.address,
        detailAddress: shippingInfo.detailAddress,
        city: shippingInfo.city || '',
        state: shippingInfo.state || '',
        zipCode: shippingInfo.zipCode,
        deliveryRequest: shippingInfo.deliveryRequest || '',
        addressEnglish: shippingInfo.addressEnglish || '',
        detailAddressEnglish: shippingInfo.detailAddressEnglish || '',
        fullAddressEnglish: shippingInfo.fullAddressEnglish || '',
        nameEnglish: shippingInfo.nameEnglish || ''
      });
    }
  }, [shippingInfo]);

  // Set payment info to form if already exists. Crypto-only at launch, so we
  // ignore any persisted non-crypto method and always coerce back to crypto.
  useEffect(() => {
    if (paymentInfo) {
      setPaymentForm({
        method: 'crypto',
        cryptoCoin: (paymentInfo.cryptoCoin as CryptoCoin) || 'BTC',
      });
    }
  }, [paymentInfo]);

  const handleShippingSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!shippingForm.name || !shippingForm.phone || !shippingForm.email) {
      toast.warning('Please fill in all required fields.');
      return;
    }

    const stateRequired = COUNTRY_CONFIGS[shippingForm.country]?.stateOptions != null;
    if (!shippingForm.address || !shippingForm.city || (stateRequired && !shippingForm.state) || !shippingForm.zipCode) {
      toast.warning('Please fill in all required fields.');
      return;
    }
    const parts: string[] = [];
    if (shippingForm.detailAddress) parts.push(shippingForm.detailAddress);
    parts.push(shippingForm.address);
    parts.push(`${shippingForm.city}, ${shippingForm.state} ${shippingForm.zipCode}`);
    const updatedForm = {
      ...shippingForm,
      addressEnglish: shippingForm.address,
      detailAddressEnglish: shippingForm.detailAddress,
      nameEnglish: shippingForm.name,
      fullAddressEnglish: parts.join(', '),
    };
    setShippingForm(updatedForm);
    setShippingInfo(updatedForm);

    setCurrentStep(2);
    window.scrollTo(0, 0);
  };

  // handlePaymentSubmit is integrated into the order button onClick handler

  const handleCouponApply = async () => {
    if (!couponCode.trim()) {
      toast.warning('Please enter a coupon code.');
      return;
    }

    const success = await applyCoupon(couponCode.trim());
    if (success) {
      toast.success('Coupon applied successfully.');
      setCouponCode('');
    }
  };

  // Auto-Apply Best Coupon
  const handleBestCouponApply = async () => {
    console.log('Auto-Apply Best Coupon button clicked');
    console.log('User object:', user);
    console.log('User ID:', user?.id);

    if (!user?.id) {
      console.error('Login required');
      toast.warning('Please log in first.');
      return;
    }

    setIsProcessing(true);
    console.log('Searching for best coupon...', { userId: user.id, amount: subtotal + shippingFee });

    try {
      const result = await couponApi.getBestCoupon(user.id, subtotal + shippingFee);
      console.log('getBestCoupon result:', result);

      if (result.bestCoupon) {
        console.log('Best coupon found:', result.bestCoupon.code);
        const success = await applyCoupon(result.bestCoupon.code);
        if (success) {
          toast.success(`Best coupon applied!\n${result.bestCoupon.name} (-$${result.maxDiscount.toLocaleString()})`);
        } else {
          console.error('applyCoupon failed');
        }
      } else {
        console.log('No available coupons');
        toast.info('No coupons available at this time.');
      }
    } catch (error) {
      console.error('Best coupon apply failed:', error);
      toast.error('An error occurred while applying the coupon.');
    } finally {
      setIsProcessing(false);
      console.log('Processing complete');
    }
  };

  const handleNameChange = (value: string) => {
    setShippingForm(prev => ({ ...prev, name: value, nameEnglish: value }));
  };

  const handleDetailAddressChange = (value: string) => {
    setShippingForm(prev => ({ ...prev, detailAddress: value }));
  };

  const handleCountryChange = (country: string) => {
    setShippingForm(prev => ({
      ...prev,
      country,
      address: '',
      detailAddress: '',
      city: '',
      state: '',
      zipCode: '',
      addressEnglish: '',
      detailAddressEnglish: '',
      fullAddressEnglish: '',
    }));
  };

  // Free zip → city/state lookup (Zippopotam.us, no API key, supports US/CA/AU/GB/FR/DE/JP)
  const handleZipAutoFill = async (zip: string) => {
    setShippingForm(prev => ({ ...prev, zipCode: zip }));
    const config = COUNTRY_CONFIGS[shippingForm.country];
    if (!config || config.zipTriggerLen === 0) return;

    const cleanZip = zip.replace(/[\s-]/g, '');
    if (cleanZip.length >= config.zipTriggerLen) {
      try {
        const apiZip = shippingForm.country === 'CA' ? cleanZip.substring(0, 3) : cleanZip;
        const res = await fetch(`https://api.zippopotam.us/${config.zippopotamCode}/${apiZip}`);
        if (res.ok) {
          const data = await res.json();
          const place = data.places?.[0];
          if (place) {
            setShippingForm(prev => ({
              ...prev,
              city: place['place name'] || prev.city,
              state: place['state abbreviation'] || place['state'] || prev.state,
            }));
          }
        }
      } catch {
        // User can fill manually
      }
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream-100">
      {/* Header */}
      <div className="bg-cream-50 border-b border-warm-200">
        <div className="section-container py-8 sm:py-12">
          <div className="flex items-center space-x-4">
            <Link to="/shop" className="text-warm-600 hover:text-forest-900">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <h1 className="font-display text-display-sm text-forest-900">Checkout</h1>
          </div>
        </div>
      </div>

      <div className="section-container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left - Order Items & Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* Order Items */}
            <div className="bg-cream-50">
              <h2 className="text-xl font-bold text-forest-900 mb-6">Order Items</h2>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="border border-warm-200 rounded-xl p-4 bg-cream-100">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src={item.image || '/images/placeholder.png'}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-lg font-medium text-forest-900">{item.name}</h3>
                            <p className="text-sm text-warm-600">{item.manufacturer}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-sm text-warm-600">
                            <span>{item.pillQuantity} pills x {item.quantity} boxes</span>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-forest-900">
                              ${(item.price * item.quantity).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Information Step */}
            {currentStep === 1 && (
              <div className="bg-cream-50">
                <h2 className="text-xl font-bold text-forest-900 mb-6">Shipping Information</h2>
                <div className="border border-warm-200 rounded-xl p-6 shadow-card">
                  <form onSubmit={handleShippingSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Recipient Name</label>
                        <input
                          type="text"
                          value={shippingForm.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          value={shippingForm.phone}
                          onChange={(e) => setShippingForm({...shippingForm, phone: e.target.value})}
                          className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-forest-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={shippingForm.email}
                        onChange={(e) => setShippingForm({...shippingForm, email: e.target.value})}
                        className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                        required
                      />
                    </div>

                    {/* Country Selector */}
                    <div>
                      <label className="block text-sm font-medium text-forest-700 mb-1">Country</label>
                      <select
                        value={shippingForm.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                      >
                        {COUNTRY_ORDER.map(code => (
                          <option key={code} value={code}>{COUNTRY_CONFIGS[code].name}</option>
                        ))}
                      </select>
                    </div>

                    {(() => {
                      const cc = COUNTRY_CONFIGS[shippingForm.country];
                      return (
                        <>
                          <div>
                            <input
                              type="text"
                              placeholder="Street Address"
                              value={shippingForm.address}
                              onChange={(e) => setShippingForm({...shippingForm, address: e.target.value})}
                              className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                              required
                            />
                          </div>

                          <div>
                            <input
                              type="text"
                              placeholder="Apt, Suite, Unit (optional)"
                              value={shippingForm.detailAddress}
                              onChange={(e) => handleDetailAddressChange(e.target.value)}
                              className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="City"
                              value={shippingForm.city}
                              onChange={(e) => setShippingForm({...shippingForm, city: e.target.value})}
                              className="px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                              required
                            />
                            {cc?.stateOptions ? (
                              <select
                                value={shippingForm.state}
                                onChange={(e) => setShippingForm({...shippingForm, state: e.target.value})}
                                className="px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 text-sm"
                                required
                              >
                                <option value="">{cc.stateLabel}</option>
                                {cc.stateOptions.map(([code, name]) => (
                                  <option key={code} value={code}>{name}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                placeholder={cc?.stateLabel || 'State/Region'}
                                value={shippingForm.state}
                                onChange={(e) => setShippingForm({...shippingForm, state: e.target.value})}
                                className="px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                              />
                            )}
                            <input
                              type="text"
                              placeholder={cc?.zipLabel || 'Zip Code'}
                              value={shippingForm.zipCode}
                              onChange={(e) => handleZipAutoFill(e.target.value)}
                              className="px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                              required
                              maxLength={cc?.zipMaxLength || 10}
                            />
                          </div>
                        </>
                      );
                    })()}

                    <button
                      type="submit"
                      className="w-full bg-forest-800 text-cream-100 py-3 rounded-lg border border-forest-700/50 hover:bg-forest-700 font-medium transition-all"
                    >
                      Next Step
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Payment Info Step */}
            {currentStep === 2 && (
              <div className="bg-cream-50">
                <div className="mb-6">
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      window.scrollTo(0, 0);
                    }}
                    className="flex items-center text-gold-400 hover:text-forest-700 text-sm font-medium"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Shipping Information
                  </button>
                </div>

                {/* Payment Method — crypto-only at launch */}
                <div className="bg-cream-50 rounded-xl border border-warm-200 p-6 shadow-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-forest-900">Payment Method</h3>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-forest-50 border border-forest-100 text-[10px] font-semibold text-forest-700 uppercase tracking-[0.12em]">
                      <span className="text-base leading-none">₿</span>
                      Cryptocurrency
                    </span>
                  </div>
                  <p className="text-[13px] text-warm-500 mb-4">
                    BTC, USDT, LTC, or XMR — the only payment rail supported right now.
                  </p>

                  {/* Coin selection */}
                  <div className="pt-4 border-t border-warm-200">
                    <p className="text-[13px] font-medium text-warm-600 mb-3">Select cryptocurrency</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {COIN_LIST.map((coin) => {
                        // PR 5: live min from NOWPayments. If order total is
                        // below the coin's min, dim the button (still selectable
                        // so the user can see why) and surface the min in red.
                        const minUsd = coinMins?.[coin.id];
                        const orderTotal = Math.max(0, subtotal + shippingFee - discount - pointsToUse);
                        const belowMin = minUsd != null && orderTotal > 0 && orderTotal < minUsd;
                        return (
                          <button
                            key={coin.id}
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, cryptoCoin: coin.id })}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                              paymentForm.cryptoCoin === coin.id
                                ? 'border-forest-600 bg-forest-50 ring-1 ring-forest-600/20'
                                : belowMin
                                ? 'border-warm-200 opacity-60 hover:opacity-100 hover:border-warm-300'
                                : 'border-warm-200 hover:border-warm-300 hover:bg-cream-100'
                            }`}
                            title={belowMin ? `Order $${orderTotal.toFixed(2)} is below ${coin.symbol} minimum $${minUsd!.toFixed(2)}` : undefined}
                          >
                            <span className={`text-lg font-bold ${coin.color}`}>
                              {COIN_ICONS[coin.id]}
                            </span>
                            <div className="text-left min-w-0">
                              <div className="text-[12px] font-semibold text-forest-900 truncate">{coin.name}</div>
                              <div className="text-[10px] text-warm-400">
                                {coin.network}
                                {minUsd != null && (
                                  <span className={`ml-1 ${belowMin ? 'text-red-500 font-semibold' : ''}`}>
                                    · min ${minUsd < 1 ? minUsd.toFixed(2) : Math.ceil(minUsd)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right - Payment Amount */}
          <div className="space-y-6">
            {/* First-time buyer promise */}
            <div className="bg-cream-100 rounded-xl border border-warm-200 p-5 shadow-card">
              <h3 className="font-display text-heading text-forest-900 italic mb-3 tracking-tight">
                First time here?
              </h3>
              <ul className="space-y-2 text-body-sm text-warm-600 leading-relaxed">
                <li className="flex gap-2.5">
                  <span className="text-gold-500 flex-shrink-0 font-semibold">·</span>
                  <span>Start small — we recommend it.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="text-gold-500 flex-shrink-0 font-semibold">·</span>
                  <span>Reship twice, free if it doesn&rsquo;t arrive.</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="text-gold-500 flex-shrink-0 font-semibold">·</span>
                  <span>Crypto refund if all three attempts fail.</span>
                </li>
              </ul>
              <Link
                to="/honestly"
                className="mt-4 inline-flex items-center gap-1.5 text-caption font-semibold text-gold-700 hover:text-gold-600 uppercase tracking-[0.15em] transition-colors"
              >
                Read why this isn&rsquo;t a scam
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>

            {/* Substitution fine print */}
            <SubstitutionNotice className="px-1" />

            {/* Coupon */}
            <div className="bg-cream-50 rounded-xl border border-warm-200 p-4 shadow-card">
              <h3 className="text-lg font-medium text-forest-900 mb-3">Coupon</h3>
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
                  <span className="text-sm font-medium text-green-800">{appliedCoupon} applied</span>
                  <button
                    onClick={removeCoupon}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Auto-Apply Best Coupon button */}
                  <button
                    onClick={handleBestCouponApply}
                    disabled={isProcessing}
                    className="w-full px-4 py-2.5 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 hover:bg-forest-700 disabled:bg-warm-400 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center space-x-2 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>{isProcessing ? 'Searching...' : 'Auto-Apply Best Coupon'}</span>
                  </button>

                  {/* Manual coupon input */}
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCouponApply()}
                      className="flex-1 px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 text-sm"
                    />
                    <button
                      onClick={handleCouponApply}
                      className="px-4 py-2 bg-cream-50 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 text-sm transition-all"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Reward Points */}
            <div className="bg-cream-50 rounded-xl border border-warm-200 p-4 shadow-card">
              <h3 className="text-lg font-medium text-forest-900 mb-3">Reward Points</h3>
              <div className="mb-3">
                <p className="text-sm text-warm-600">
                  Available Points: <span className="font-bold text-gold-400">{availablePoints.toLocaleString()} pts</span>
                </p>
              </div>
              <div className="grid grid-cols-[1fr,auto] gap-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Points to use"
                  value={pointsToUse || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    const maxUsable = Math.floor(Math.min(availablePoints, subtotal + shippingFee - discount));
                    setPointsToUse(Math.max(0, Math.min(val, maxUsable)));
                  }}
                  className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 text-sm"
                  max={Math.floor(Math.min(availablePoints, subtotal + shippingFee - discount))}
                />
                <button
                  onClick={() => {
                    setPointsToUse(Math.max(0, Math.floor(Math.min(availablePoints, subtotal + shippingFee - discount))));
                  }}
                  className="px-4 py-2 bg-cream-50 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 text-sm whitespace-nowrap transition-all"
                >
                  Use All
                </button>
              </div>
              {pointsToUse > 0 && (
                <p className="text-sm text-gold-400 mt-2">
                  {pointsToUse.toLocaleString()}P used (Discount: -${pointsToUse.toLocaleString()})
                </p>
              )}
            </div>

            {/* Payment Amount */}
            <div className="bg-cream-100 rounded-xl border border-warm-200 p-6 shadow-subtle">
              <h3 className="text-xl font-bold text-forest-900 mb-6">Payment Amount</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2">
                  <span className="text-forest-700">Subtotal</span>
                  <span className="text-lg font-medium text-forest-900">${subtotal.toLocaleString()}</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-forest-700">Shipping</span>
                    <span className="text-lg font-medium text-forest-900">
                      {shippingFee === 0 ? 'Free Shipping' : `$${shippingFee.toLocaleString()}`}
                    </span>
                  </div>
                  
                  {/* Free Shipping Progress */}
                  {(() => {
                    const effectiveAmount = subtotal; // Pre-discount subtotal (industry standard)
                    const freeShippingThreshold = FREE_SHIPPING_THRESHOLD;
                    const isEligible = effectiveAmount >= freeShippingThreshold;

                    return isEligible ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm font-medium text-green-900">Free shipping unlocked!</span>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-forest-50 border border-forest-200 rounded-lg p-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-forest-900">Free Shipping Progress</span>
                            <span className="text-xs text-gold-400">
                              ${effectiveAmount.toLocaleString()} / ${freeShippingThreshold.toLocaleString()}
                            </span>
                          </div>
                          <div className="w-full bg-forest-200 rounded-full h-2">
                            <div
                              className="bg-gold-400 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min((effectiveAmount / freeShippingThreshold) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-forest-700 text-center">
                            ${(freeShippingThreshold - effectiveAmount).toLocaleString()} more for free shipping
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {(discount + pointsToUse) > 0 && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-forest-700">Discount</span>
                    <span className="text-lg font-medium text-gold-400">-${(discount + pointsToUse).toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-warm-200 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-forest-900">Order Total</span>
                    <span className="text-2xl font-bold text-gold-400">${Math.max(0, subtotal + shippingFee - discount - pointsToUse).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              {/* Place Order Button */}
              {currentStep === 2 && (
                <div className="mt-6">
                  {/* Import compliance acknowledgment — required before order can be placed */}
                  <div className="mb-4 bg-cream-50 border border-warm-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importAck}
                        onChange={(e) => setImportAck(e.target.checked)}
                        className="w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-xs text-warm-600 leading-relaxed">
                        I understand that import compliance is my responsibility under my country&rsquo;s
                        personal-use regulations, and the products I order are for my personal use only.{' '}
                        <Link
                          to="/policy/import-legality"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold-700 hover:text-gold-600 underline"
                        >
                          Read the rules
                        </Link>.
                      </span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!importAck) {
                        toast.warning('Please acknowledge import responsibility before placing your order.');
                        return;
                      }
                      setIsProcessing(true);
                      try {
                        if (!user) {
                          toast.warning('Please log in first.');
                          navigate('/login');
                          return;
                        }

                        devLog.info('Starting order creation...');
                        devLog.info('User:', user);
                        devLog.info('Cart items:', items);
                        devLog.info('Shipping Information:', shippingForm);

                        // New approach: RPC handles Coupon/Reward Points atomically
                        const orderResult = await orderCreationApi.createOrder(user.id, {
                          items: items.map(item => ({
                            productId: item.productId,
                            name: item.name,
                            manufacturer: item.manufacturer,
                            quantity: item.quantity,
                            pillQuantity: item.pillQuantity,
                            price: item.price,
                            image: item.image
                          })),
                          totalAmount: Math.max(0, subtotal + shippingFee - discount - pointsToUse),
                          shippingFee: shippingFee,
                          discount: discount + pointsToUse,
                          shippingInfo: {
                            name: shippingForm.name,
                            phone: shippingForm.phone,
                            email: shippingForm.email,
                            country: shippingForm.country,
                            address: shippingForm.address,
                            detailAddress: shippingForm.detailAddress,
                            city: shippingForm.city,
                            state: shippingForm.state,
                            zipCode: shippingForm.zipCode,
                            deliveryRequest: shippingForm.deliveryRequest,
                            addressEnglish: shippingForm.addressEnglish,
                            detailAddressEnglish: shippingForm.detailAddressEnglish,
                            fullAddressEnglish: shippingForm.fullAddressEnglish,
                            nameEnglish: shippingForm.nameEnglish
                          },
                          paymentMethod: paymentForm.method,
                          // RPC validates and processes internally
                          couponId: appliedCouponData?.id as string | undefined,
                          pointsUsed: pointsToUse > 0 ? pointsToUse : undefined
                        });

                        if (!orderResult.success) {
                          throw new Error(orderResult.error || 'Order creation failed');
                        }

                        devLog.info('Order created successfully (Coupon/Points auto-processed):', orderResult);

                        if (orderResult && orderResult.orderId) {
                          devLog.info('Order creation complete:', orderResult.orderNumber);
                          devLog.info('Email auto-sent: DB trigger processing...');

                          // Referral reward processing (issue coupon to referrer on friend's first purchase)
                          try {
                            await referralApi.rewardReferrerOnFirstPurchase(user.id, orderResult.orderId);
                            devLog.info('Referral reward processed');
                          } catch (error) {
                            devLog.error('Referral reward processing error (ignored):', error);
                          }

                          // NOTE: cart is intentionally NOT cleared here. Standard e-commerce
                          // flow clears cart only after payment succeeds — clearing on order
                          // creation would empty the cart even if invoice creation or payment
                          // fails, leaving the user with no recoverable state.
                          // Cart clear cascade (all unified via DB trigger trg_clear_cart_on_paid):
                          //   - crypto: webhook + check-status update orders.payment_status='paid'
                          //   - bank: admin manual confirmation updates orders.payment_status='paid'
                          //   - frontend (CryptoPayment.tsx) also calls clearCart() for UX immediacy

                          // Navigate based on payment method
                          if (paymentForm.method === 'crypto') {
                            navigate(`/crypto-payment/${orderResult.orderId}?coin=${paymentForm.cryptoCoin}`, { replace: true });
                          } else {
                            navigate(`/contact-method/${orderResult.orderId}`, { replace: true });
                          }
                        } else {
                          throw new Error('Order creation failed');
                        }
                        
                      } catch (error) {
                        devLog.error('Order processing error:', error);
                        const msg = (error as Error).message || '';
                        if (msg.includes('coupon_not_owned_or_already_used')) {
                          toast.error('This coupon is no longer available. Please try a different one.');
                        } else if (msg.includes('insufficient_point_balance')) {
                          toast.error('Not enough points for this order.');
                        } else {
                          toast.error('An error occurred processing your order. Please try again.');
                        }
                      } finally {
                        setIsProcessing(false);
                      }
                    }}
                    disabled={isProcessing || !importAck}
                    className="w-full bg-forest-800 text-cream-100 py-4 px-6 rounded-lg border border-forest-700/50 font-bold text-lg hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-card"
                  >
                    {isProcessing ? 'Processing...' : 'Place Order'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;